import * as Popover from "@radix-ui/react-popover";
import { useMutation } from "@tanstack/react-query";
import React, { type PropsWithChildren, useEffect, useState } from "react";

import { ModalBase } from "@performyard/py-components";
import type { FormQuestionType } from "@performyard/service-contracts";

import { Content } from "./Content";
import { Header } from "./Header";

import type { FormQuestionRephraseResponse, RephraseBlockData } from "./types";

import { postQueryAi } from "../../../api/services";

import { extractPreviousResponses } from "./utils";

export type FormQuestionRephraseModuleProps = {
	input: string;
	questionType: FormQuestionType;
	onInsert: (newAnswer: string) => void;
	isOpen?: boolean;
	onOpenChange?: (isOpen: boolean) => void;
};

const MAX_RETRY_COUNT = 25;

export const FormQuestionRephraseModule = (
	props: PropsWithChildren<FormQuestionRephraseModuleProps>
): React.ReactElement => {
	const { input, questionType, onInsert, isOpen: propIsOpen, onOpenChange } = props;

	const [isExpand, setExpand] = useState(false);
	const [rephrasedData, setRephrasedData] = useState<RephraseBlockData[]>([{ data: "", inProgress: true }]);
	const [isOpen, setIsOpen] = useState(propIsOpen || false);
	const [isRetryCount, setRetryCount] = useState(0);
	const isRetryDisabled = isRetryCount >= MAX_RETRY_COUNT;

	const handleToggleExpand = (): void => {
		setExpand((prev) => !prev);
	};

	const handleDismiss = (): void => {
		setIsOpen(false);
		onOpenChange?.(false);
	};

	const handleInsert = (newAnswer: string): void => {
		onInsert(newAnswer);
		handleDismiss();
	};

	const handleRephraseDismiss = (index: number): void => {
		setRephrasedData((prev) => prev.filter((_, i) => i !== index));
	};

	const handleRetry = (): void => {
		if (isRetryDisabled) return;
		setRetryCount((prevCount) => prevCount + 1);
		setRephrasedData((prev) => [...prev, { data: "", inProgress: true }]);
		const previousResponses = extractPreviousResponses(rephrasedData);
		rephraseMutation.mutate({ input, recreate: true, previousResponses });
	};

	const rephraseMutation = useMutation({
		mutationFn: async ({
			input,
			recreate,
			previousResponses = [],
		}: {
			input: string;
			recreate?: boolean;
			previousResponses?: string[];
		}) => {
			return await postQueryAi<FormQuestionRephraseResponse>({
				type: "form-question-rephrase",
				input,
				questionType,
				findAdditionalContext: false,
				retry: recreate ? "true" : "false",
				responseStyle: "structured",
				previousResponses,
			});
		},
		onSuccess: (response) => {
			let newData: string;
			if ("message" in response) {
				newData = response.message || "Error rephrasing response";
			} else {
				const answer = response.data?.answer;
				if (Array.isArray(answer)) {
					newData = answer[0] ?? "No rephrased answer received.";
				} else {
					newData = answer || "No rephrased answer received.";
				}
			}

			setRephrasedData((prev) => {
				const updated = [...prev];
				updated[updated.length - 1] = { data: newData, inProgress: false };
				return updated;
			});
		},
		onError: () => {
			setRephrasedData((prev) => {
				const updated = [...prev];
				updated[updated.length - 1] = { data: "Error rephrasing response", inProgress: false };
				return updated;
			});
		},
	});

	useEffect(() => {
		rephraseMutation.mutate({ input });
	}, [input]);

	return (
		<div>
			{!isExpand && (
				<Popover.Root open={isOpen} onOpenChange={handleDismiss}>
					<Popover.Trigger asChild>
						<div />
					</Popover.Trigger>
					<Popover.Portal container={document.body}>
						<Popover.Content
							className="z-[9999] w-[416px] rounded-lg bg-white p-4 shadow-[0_4px_30px_0_rgba(0,0,0,0.15)] transition-all duration-500"
							sideOffset={26}
							side="top"
							align="end"
							sticky="always"
							hideWhenDetached={true}
							data-testid="form-question-rephrase-content"
							avoidCollisions={false}
						>
							<div className="max-h-[450px] min-h-full overflow-hidden bg-white">
								<Header
									onDismiss={handleDismiss}
									onExpand={handleToggleExpand}
									isRetryDisabled={isRetryDisabled}
									isExpand={isExpand}
								/>

								<Content
									input={input}
									rephrasedData={rephrasedData}
									onInsert={handleInsert}
									handleRephraseDismiss={handleRephraseDismiss}
									handleRetry={handleRetry}
									handleDismiss={handleDismiss}
									retryDisabled={isRetryDisabled}
									height="225px"
									isExpanded={isExpand}
								/>
							</div>

							<Popover.Arrow className="fill-white" offset={26} height={8} width={16} />
						</Popover.Content>
					</Popover.Portal>
				</Popover.Root>
			)}

			{isExpand && (
				<ModalBase isOpen={isExpand} onClose={handleDismiss} dangerouslySetClassName="h-full max-h-full w-full">
					<div data-testid="expanded-section" className="h-full p-4">
						<Header
							onDismiss={handleDismiss}
							onExpand={handleToggleExpand}
							isRetryDisabled={isRetryDisabled}
							isExpand={isExpand}
						/>
						<Content
							input={input}
							rephrasedData={rephrasedData}
							onInsert={handleInsert}
							handleRephraseDismiss={handleRephraseDismiss}
							handleRetry={handleRetry}
							handleDismiss={handleDismiss}
							retryDisabled={isRetryDisabled}
							height="577px"
							isExpanded={isExpand}
						/>
					</div>
				</ModalBase>
			)}
		</div>
	);
};
